<template>
  <Modal
    :show="showModal && !!selectedMeeting"
    size="md"
    :zIndex="10080"
    dialog-max-width="560px"
    content-max-height="75vh"
    @close="closeModal"
    title="Schedule a Meeting Reminder"
  >
    <Form
      v-if="selectedMeeting"
      id="schedule-meeting-reminder-form"
      class="form"
      @submit="handleSubmit"
    >
      <div class="modal-body-scroll">
        <!-- Meeting Details Card -->
        <div class="meeting-details-card">
          <div class="detail-row">
            <span class="detail-label">Meeting Title</span>
            <router-link
              :to="{ name: MEETINGS_ROUTE, params: { id: selectedMeeting.id } }"
              class="detail-value link"
            >
              {{ selectedMeeting.title }}
            </router-link>
          </div>

          <div class="detail-row">
            <span class="detail-label">Duration</span>
            <span class="detail-value">{{ selectedMeeting.duration }} min</span>
          </div>

          <div class="detail-row">
            <span class="detail-label">Scheduled on</span>
            <span class="detail-value">
              {{ formatDate(selectedMeeting.meetingDateTime) }}
            </span>
          </div>

          <div class="detail-row">
            <span class="detail-label">Guests</span>
            <span class="detail-value">
              <template v-if="(selectedMeeting.guestEmails ?? []).length > 0">
                {{ (selectedMeeting.guestEmails ?? []).join(", ") }}
              </template>
              <span v-else class="text-muted">—</span>
            </span>
          </div>

          <div class="detail-row">
            <span class="detail-label">Meeting Link</span>
            <div v-if="selectedMeeting.meetingURL" class="detail-value link-row">
              <a
                :href="selectedMeeting.meetingURL"
                target="_blank"
                rel="noopener noreferrer"
                class="meeting-link"
              >
                {{ truncateUrl(selectedMeeting.meetingURL) }}
              </a>
              <button
                type="button"
                class="icon-btn"
                @click.stop="openLink(selectedMeeting.meetingURL)"
              >
                <Icon icon="mdi:open-in-new" />
              </button>
              <button
                type="button"
                class="icon-btn"
                @click.stop="copyToClipboard(selectedMeeting.meetingURL)"
              >
                <Icon icon="mdi:content-copy" />
              </button>
            </div>
            <span v-else class="detail-value text-muted">—</span>
          </div>
        </div>

        <!-- Form Fields -->
        <div class="form-grid">
          <div class="form-group">
            <Field
              name="bucketOptionId"
              :rules="{ required: ['Bucket'] }"
              v-model="formData.bucketOptionId"
              v-slot="{ field }"
            >
              <Select
                :model-value="field.value"
                @update:model-value="field.onChange($event)"
                @blur="field.onBlur"
                :options="bucketOptionsForSelect"
                label="Bucket"
                placeholder="Select bucket"
                required
              />
            </Field>
            <ErrorMessage name="bucketOptionId" class="error-message" />
          </div>

          <div class="form-group">
            <Field
              name="taskType"
              :rules="{ required: ['Task Type'] }"
              v-model="formData.taskType"
              v-slot="{ field }"
            >
              <Select
                :model-value="field.value"
                @update:model-value="field.onChange($event)"
                @blur="field.onBlur"
                :options="taskTypeOptions"
                label="Task Type"
                placeholder="Select task type"
                required
              />
            </Field>
            <ErrorMessage name="taskType" class="error-message" />
          </div>

          <div class="form-group">
            <Select
              v-model="selectedFollowUpOption"
              :options="followUpOptions"
              label="Follow up in"
              placeholder="Select follow up option"
              @update:model-value="updateFollowUpDate"
            />
          </div>

          <div class="form-group">
            <Field
              name="followUpDate"
              :rules="{ required: ['Follow up Date'] }"
              v-model="formData.followUpDate"
              v-slot="{ field }"
            >
              <DateSelector
                :model-value="field.value"
                @update:model-value="field.onChange($event)"
                @blur="field.onBlur"
                label="Follow up Date"
                :disable-past="false"
                :max-date="meetingDateIso"
                required
              />
            </Field>
            <ErrorMessage name="followUpDate" class="error-message" />
          </div>
        </div>

        <p v-if="isFollowUpAfterMeeting" class="after-meeting-warning">
          Follow-up date must be on or before the meeting date.
        </p>
      </div>
    </Form>

    <template #footer>
      <div class="modal-actions">
        <Button type="button" variant="secondary" block @click="closeModal" :disabled="isLoading">
          Cancel
        </Button>
        <Button
          type="submit"
          form="schedule-meeting-reminder-form"
          variant="primary"
          block
          :loading="isLoading"
          :disabled="isLoading || isFollowUpAfterMeeting"
        >
          Save
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { Icon } from "@iconify/vue";
import { Field, Form, ErrorMessage, defineRule } from "vee-validate";
import { Clipboard } from "v-clipboard";
import { Button, Modal, Select, DateSelector } from "@/components/ui";
import {
  useBuckets,
  useBucketOptions,
  useRegisterBucket,
  useUpdateBucket,
} from "@/composables/bucket";
import { MEETINGS_ROUTE } from "@/router/routes";
import { TaskType } from "@/utils/enums";
import { getTaskTypeIcon } from "@/utils/iconMapping";

defineRule("required", (value: any, [fieldName = "Field"]: string[]) => {
  if (!value || value.length === 0) {
    return `${fieldName} is required`;
  }
  return true;
});

interface Props {
  selectedMeeting?: {
    id: string;
    title: string;
    duration: number;
    meetingDateTime: string;
    guestEmails?: string[];
    meetingURL?: string;
    contactId: string;
  };
}

const props = defineProps<Props>();
const emit = defineEmits<{
  closeScheduleMeetingReminderModal: [];
}>();

const router = useRouter();
const store = useStore();

const showModal = ref(true);
const selectedFollowUpOption = ref<string | number | undefined>(undefined);

const account = computed(() => store.getters.account.data);
const organizationId = computed(() => account.value?.organizationId);

// Form data
const formData = ref({
  id: null as string | null,
  taskType: null as string | null,
  followUpDate: null as string | null,
  bucketOptionId: null as string | null,
  contactIds: [] as string[],
  organizationId: null as string | null,
});

// Queries
const bucketParams = computed(() =>
  organizationId.value && props.selectedMeeting?.contactId
    ? { organizationId: organizationId.value, contactId: props.selectedMeeting.contactId }
    : null
);

const { data: bucketsData } = useBuckets(bucketParams);
const { data: bucketOptionsData } = useBucketOptions(
  computed(() => (organizationId.value ? { organizationId: organizationId.value } : null))
);

// Mutations
const { mutateAsync: registerBucket, isPending: isRegistering } = useRegisterBucket();
const { mutateAsync: updateBucket, isPending: isUpdating } = useUpdateBucket();

const isLoading = computed(() => isRegistering.value || isUpdating.value);

// Computed
const existingBucket = computed(() => {
  if (!bucketsData.value?.data?.length) return null;
  return bucketsData.value.data[0];
});

const bucketOptionsForSelect = computed(() => {
  if (!bucketOptionsData.value) return [];
  return bucketOptionsData.value.map((opt: any) => ({
    value: opt.id,
    label: opt.name,
  }));
});

const taskTypeOptions = computed(() =>
  Object.values(TaskType).map((task) => ({
    value: task,
    label: task.charAt(0).toUpperCase() + task.slice(1),
    icon: getTaskTypeIcon(task),
  }))
);

const followUpOptions = [
  { value: "1", label: "Tomorrow" },
  { value: "3", label: "In 3 Days" },
  { value: "7", label: "In 1 Week" },
  { value: "30", label: "In 1 Month" },
  { value: "90", label: "In 3 Months" },
  { value: "180", label: "In 6 Months" },
  { value: "365", label: "In 1 Year" },
];

// Get meeting date for comparison (date only, no time)
const meetingDate = computed(() => {
  if (!props.selectedMeeting?.meetingDateTime) return null;
  const date = new Date(props.selectedMeeting.meetingDateTime);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
});

// Meeting date as YYYY-MM-DD for DateSelector maxDate
const meetingDateIso = computed(() => {
  if (!meetingDate.value) return undefined;
  return meetingDate.value.toISOString().split("T")[0];
});

const isFollowUpAfterMeeting = computed(() => {
  if (!meetingDate.value || !formData.value.followUpDate) return false;
  const followUp = new Date(formData.value.followUpDate);
  return followUp > meetingDate.value;
});

// Methods
const formatDate = (dateString: string) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const truncateUrl = (url: string) => {
  if (url.length > 35) {
    return url.substring(0, 35) + "...";
  }
  return url;
};

const openLink = (url: string) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

const copyToClipboard = (value: string) => {
  Clipboard.copy(value);
};

const updateFollowUpDate = (value: string | number) => {
  if (value == null || value === "") return;
  const daysToAdd = parseInt(String(value), 10);
  const date = new Date();
  date.setDate(date.getDate() + daysToAdd);
  const localMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  if (meetingDate.value && localMidnight > meetingDate.value) {
    formData.value.followUpDate = meetingDate.value.toISOString();
  } else {
    formData.value.followUpDate = localMidnight.toISOString();
  }
};

const setInitialValues = () => {
  if (!props.selectedMeeting) return;

  // Always set these for creating new buckets
  formData.value.contactIds = [props.selectedMeeting.contactId];
  formData.value.organizationId = organizationId.value;

  if (existingBucket.value) {
    formData.value.id = existingBucket.value.id;
    formData.value.taskType = existingBucket.value.taskType;
    formData.value.bucketOptionId = existingBucket.value.bucketOptionId;
    if (existingBucket.value.followUpDate) {
      formData.value.followUpDate = existingBucket.value.followUpDate;
    }
  }
};

// Watch for bucket data to load and update form values
watch(
  existingBucket,
  (bucket) => {
    if (bucket) {
      formData.value.id = bucket.id;
      formData.value.taskType = bucket.taskType;
      formData.value.bucketOptionId = bucket.bucketOptionId;
      if (bucket.followUpDate) {
        formData.value.followUpDate = bucket.followUpDate;
      }
    }
  },
  { immediate: true }
);

const handleSubmit = async () => {
  if (isFollowUpAfterMeeting.value) {
    return;
  }

  try {
    if (existingBucket.value && formData.value.id) {
      await updateBucket({
        id: formData.value.id,
        taskType: formData.value.taskType!,
        followUpDate: formData.value.followUpDate!,
        bucketOptionId: formData.value.bucketOptionId!,
      });
    } else {
      await registerBucket({
        taskType: formData.value.taskType!,
        followUpDate: formData.value.followUpDate!,
        bucketOptionId: formData.value.bucketOptionId!,
        contactIds: formData.value.contactIds,
        organizationId: formData.value.organizationId!,
      });
    }
    closeModal();
    router.push({ name: MEETINGS_ROUTE });
  } catch (error: any) {
    // Save failed
  }
};

const closeModal = () => {
  emit("closeScheduleMeetingReminderModal");
};

// Watch for bucket data and set initial values
onMounted(() => {
  setInitialValues();
});
</script>

<style scoped>
.form {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.modal-body-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px 16px;
}

.meeting-details-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 400;
}

.detail-value {
  font-size: 13px;
  color: #3e3efe;
  font-weight: 500;
  text-align: right;
  max-width: 60%;
}

.detail-value.link {
  text-decoration: none;
}

.detail-value.link:hover {
  text-decoration: underline;
}

.text-muted {
  color: #9ca3af;
}

.link-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.meeting-link {
  color: #3e3efe;
  text-decoration: none;
  font-size: 13px;
}

.meeting-link:hover {
  text-decoration: underline;
}

.icon-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.15s ease;
}

.icon-btn:hover {
  color: #3e3efe;
  background: #f3f4f6;
}

.icon-btn .iconify {
  font-size: 16px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-grid :deep(.ui-select__required),
.form-grid :deep(.date-selector__required) {
  font-size: inherit;
  line-height: inherit;
}

.error-message {
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
}

.modal-actions {
  display: flex;
  gap: 12px;
  width: 100%;
}

.modal-actions > * {
  flex: 1;
}

@media (max-width: 640px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

.after-meeting-warning {
  margin: 0 0 16px;
  font-size: 12px;
  color: #b45309;
}
</style>
